<script setup lang="ts">
import { computed } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import type { Team } from "@/model/session";
import { useBoardsStore } from "@/store/boards";
import { isUserTeam } from "@/store/team";
import { useWorkModeStore } from "@/store/workMode";

import ArtObjectiveCell from "./ArtObjectiveCell.vue";

const props = defineProps<{ team: Team }>();

const boardsStore = useBoardsStore();
const board = computed(() => boardsStore.boardByType("team", { teamId: props.team.id }));
const isEmptyTeam = computed(
  () => board.value.objectives.length === 0 && board.value.stretchObjectives.length === 0,
);
const teamCount = computed(
  () => board.value.objectives.length + board.value.stretchObjectives.length,
);
const belongsToUser = computed(() => isUserTeam(props.team));
// One objective with an AV reserves the slot for the whole team, so the
// attribute rows line up. 0 is a deliberate score, only null is unset.
const reserveActualValue = computed(
  () =>
    useWorkModeStore().isExecutionMode &&
    [...board.value.objectives, ...board.value.stretchObjectives].some(
      (objective) => objective.av !== null,
    ),
);
</script>

<template>
  <section class="team-block" :aria-label="team.name">
    <header class="team-header" :class="{ 'user-team': belongsToUser }">
      <BaseTooltip class="team-name" :content="team.name" only-show-on-overflow>
        {{ team.name }}
      </BaseTooltip>
      <span v-if="isEmptyTeam" class="empty-message">
        {{ $t("objectives.none") }}
      </span>
      <span v-else class="team-count">{{ teamCount }}</span>
    </header>

    <template v-if="!isEmptyTeam">
      <div class="section-header">
        <span class="section-title">{{ $t("objectives.committed") }}</span>
        <span class="section-count">{{ board.objectives.length }}</span>
      </div>
      <div v-for="(objective, idx) in board.objectives" :key="objective.id" class="objective-cell">
        <ArtObjectiveCell :objective :board :index="idx + 1" :reserve-actual-value />
      </div>

      <div class="section-header">
        <span class="section-title">{{ $t("objectives.uncommitted") }}</span>
        <span class="section-count">{{ board.stretchObjectives.length }}</span>
      </div>
      <div
        v-for="(objective, idx) in board.stretchObjectives"
        :key="objective.id"
        class="objective-cell"
      >
        <ArtObjectiveCell :objective :board :index="idx + 1" :reserve-actual-value />
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
@use "@/styles/board";
@use "@/styles/font";
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";

$team-margin-lr: 8px;
$team-padding-lr: 16px;
$team-padding-tb: 12px;
$section-margin-lr: 18px;

// Adjustment to make the team header more readable at small scales. Mirrors teamHeaderScale() in computeLayout.ts.
$team-header-scale: var(--art-header-scale) / var(--art-scale);

.team-block {
  display: flex;
  flex-direction: column;
  width: 100%;
}

// Sibling gaps must mirror GAP_* in computeLayout.ts.
.team-block > * + * {
  margin-top: board.len(16px);
}

.team-block > .section-header + .objective-cell {
  margin-top: board.len(4px);
}

.team-block > .objective-cell + .objective-cell {
  margin-top: 0;
}

.team-block > .objective-cell + .section-header {
  margin-top: board.len(10px);
}

.team-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: board.len(8px);
  margin-left: board.len($team-margin-lr);
  margin-right: board.len($team-margin-lr);
  padding: board.len($team-padding-tb) board.len($team-padding-lr);
  min-width: 0;
  background-color: colors.$background-grey;
  border-radius: board.len(6px);

  &.user-team {
    background-color: colors.$background-ui-inverse;
    color: colors.$text-inverse;

    .empty-message {
      color: colors.$text-subtle-inverse;
    }
  }

  .team-name {
    @include utils.ellipsis;

    font-size: calc(#{board.len(16px)} * $team-header-scale);
    line-height: 1.4;
    font-weight: font.$weight-bold;
    min-width: 0;
  }

  .team-count {
    font-size: calc(#{board.len(13px)} * $team-header-scale);
    font-weight: font.$weight-semi-bold;
  }

  // Right-aligned via the team-header's `justify-content: space-between`.
  // line-height 1.4 leaves room below the baseline for the 'y' descender in
  // "No objectives yet" (1.0 would clip).
  .empty-message {
    font-size: calc(#{board.len(12px)} * $team-header-scale);
    line-height: 1.4;
    font-weight: font.$weight-semi-bold;
    color: colors.$text-subtle;
    flex-shrink: 0;
  }
}

// margin-left/right separately (not the `margin` shorthand) so the sibling
// margin-top rules above aren't overridden by an implicit margin-top: 0.
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1.2;
  margin-left: board.len($section-margin-lr);
  margin-right: board.len($section-margin-lr);

  .section-title,
  .section-count {
    font-size: board.len(13px);
    font-weight: font.$weight-semi-bold;
  }

  .section-count {
    color: colors.$text-subtle;
  }
}

// 6 cell margin + 12 internal card padding = 18 — same inset as .team-header
// and .section-header, so index/title/meta-row align with the labels above.
.objective-cell {
  height: var(--cell-height);
  margin-left: board.len(6px);
  margin-right: board.len(6px);
  overflow: hidden;
}
</style>
